import React, { useState, useRef } from 'react';
import InlineHelpText from '../../shared/InlineHelpText';
import HelpIcon from '../../shared/HelpIcon';
import { FormattedMessage, FormattedDate } from 'react-intl';
import SvgExclamationPointIcon from '../../svg/ExclamationPointIcon';
import { Link } from 'react-router-dom';
import { timeConvert } from '../../../utilities/general';
import AccessControl from '../../../AccessControl';
import permissions from '../../../permissionsList';
import Table from '../../shared/table/Table';
import {
  typeText,
  descriptionText,
} from '../../shared/SharedInternationalizations';
import SvgOpenExternal from '../../svg/OpenExternal';
import { connect } from 'react-redux';

export function UnconnectedUserCheckinPreferences(props) {
  const blankProfile = {
    user: '',
    receive_checkin_reminder: null,
    call_for_checkin: null,
    call_for_checkin_time: null,
    exclusion_start_time: null,
    exclusion_end_time: null,
    exclusion_start_status: null,
    exclusion_end_status: null,
    checkin_modes: [],
    checkin_device: null,
  };

  const {
    user,
    receive_checkin_reminder,
    call_for_checkin,
    call_for_checkin_time,
    exclusion_start_time,
    exclusion_end_time,
    exclusion_start_status,
    exclusion_end_status,
    checkin_modes,
    checkin_device,
  } = props.checkin_profile || blankProfile;
  const {
    checkin_profile,
    userStatuses,
    currentLocation,
    checkinModes,
    userObj,
    unitsObj,
    alertDeviceTypes,
    unitCheckin,
    checkinCall = 15,
  } = props;

  let checkInCallTime;
  if (call_for_checkin_time) {
    checkInCallTime = timeConvert(call_for_checkin_time);
  }

  const [showCheckInTimeHelpText, setShowCheckInTimeHelpText] = useState(false);
  function toggleCheckInTimeHelpText(value) {
    setShowCheckInTimeHelpText(value);
  }
  const checkInTimeHelpTextRef = useRef();
  const checkInTimeHelpText = (
    <FormattedMessage
      id="userCheckinPreferences.checkInTimeHelpText"
      defaultMessage="This is the time to call this user to check them in."
      description="Help text for understanding what the check-in modes are."
    />
  );

  const [showCheckInModesHelpText, setShowCheckInModesHelpText] = useState(
    false
  );
  function toggleCheckInModesHelpText(value) {
    setShowCheckInModesHelpText(value);
  }
  const checkInModesHelpTextRef = useRef();
  const checkInModesHelpText = (
    <FormattedMessage
      id="userCheckinPreferences.checkInModesHelpText"
      defaultMessage="These are the check-in modes for this user."
      description="Help text for understanding what the check-in modes mean."
    />
  );

  const [showCheckInExcludeHelpText, setShowCheckInExcludeHelpText] = useState(
    false
  );
  function toggleShowCheckInExcludeHelpText(value) {
    setShowCheckInExcludeHelpText(value);
  }
  const checkInExcludeHelpTextRef = useRef();
  const checkInExcludeHelpText = (
    <FormattedMessage
      id="userCheckinPreferences.checkInExcludeHelpText"
      defaultMessage="During this time-frame, the user will not be required to check-in. There will be no check-in prompts or alerts. This is used for when a user is going to be away from a location where checking-in would be required, such as in another facility."
      description="Help text for understanding what the user check-in exclude means."
    />
  );

  const [showCheckinDeviceHelpText, setShowCheckinDeviceHelpText] = useState(
    false
  );
  function toggleCheckinDeviceHelpText(value) {
    setShowCheckinDeviceHelpText(value);
  }
  const checkinDeviceHelpTextRef = useRef();
  const checkinDeviceHelpText = (
    <FormattedMessage
      id="userCheckinPreferences.checkinDeviceHelpText"
      defaultMessage="When a user needs to be called to check in, this device will be what SARA initiates the call to."
      description="Help text for understanding what the check-in modes are."
    />
  );
  function unitCheckinCheck() {
    let unitCheckinInfo = '';
    if (unitCheckin) {
      unitCheckinInfo = (
        <div className="content-display__column">
          <div className="content-display__item-pill-container">
            <div className="item-container__info-container">
              <SvgExclamationPointIcon className="text-alert" />
            </div>
            <div className="item-container__main-container">
              <div className="text-large text-light text-alert align-center">
                <FormattedMessage
                  data-test="unit-checkin-message"
                  id="userCheckinPreferences.unitCheckinMessageText"
                  defaultMessage="This user will be checked in by their primary unit: <b>unit</b>"
                  description="The check-in instructions"
                  values={{
                    b: (msg) => (
                      <Link
                        className="link-button button-danger margin-left-10 padding-10"
                        to={`/units/${
                          unitsObj[userObj.active_unit].id
                        }/view/?view=checkin`}>
                        <span
                          className="button__text"
                          data-test="checkin-reminder-message-text">
                          {unitsObj[userObj.active_unit].name}
                          <SvgOpenExternal className="margin-left-10 text-small" />
                        </span>
                      </Link>
                    ),
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    return unitCheckinInfo;
  }

  const checkInReminder = (
    <div className="content-display__column">
      <div className="content-display__item-pill-container">
        {receive_checkin_reminder ? (
          <React.Fragment>
            <div className="item-container__info-container">
              <SvgExclamationPointIcon className="text-alert" />
            </div>
            <div className="item-container__main-container">
              <div className="text-large text-light text-alert">
                <FormattedMessage
                  data-test="checkin-reminder-message"
                  id="userCheckinPreferences.callUserCheckInText"
                  defaultMessage="Call this user <b>minutes</b> prior to the end of the check-in period if no check-in has been received"
                  description="The check-in instructions"
                  values={{
                    b: (msg) => (
                      <span
                        className="item-container__text-highlighted item-container__text-highlighted--alert"
                        data-test="checkin-reminder-message-text">
                        {checkinCall} {msg}
                      </span>
                    ),
                  }}
                />
              </div>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div className="item-container__info-container"></div>
            <div className="item-container__main-container">
              <div className="text-large text-light">
                <FormattedMessage
                  data-test="checkin-reminder-message-false"
                  id="userCheckinPreferences.callUserCheckInTextFalse"
                  defaultMessage="This user will <b>not</b> be called prior to the end of the check-in period if no check-in has been received"
                  description="The check-in instructions"
                  values={{
                    b: (msg) => (
                      <span
                        className="item-container__text-highlighted"
                        data-test="checkin-reminder-message-text-false">
                        {msg}
                      </span>
                    ),
                  }}
                />
              </div>
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );

  const checkinTime = call_for_checkin ? (
    <div
      className="content-display__column content-display__column--1-2"
      data-test="call-for-checkin-container">
      <div className="content-display__item-pill-container">
        <div className="item-container__main-container">
          <div className="item-container__main-text text-large text-light">
            <FormattedMessage
              data-test="call-for-checkin"
              id="userCheckinPreferences.checkInTimeText"
              defaultMessage="Call at <b>a</b> to check in this user"
              description="The check-in instructions"
              values={{
                b: (msg) => (
                  <span
                    className="item-container__text-highlighted"
                    data-test="call-for-checkin-text">
                    {checkInCallTime}
                  </span>
                ),
              }}
            />
          </div>
        </div>
        <div className="item-container__info-container">
          <HelpIcon
            styles="margin-left-5"
            onClick={() => checkInTimeHelpTextRef.current.toggleHelpText()}
            toggled={showCheckInTimeHelpText}
          />
        </div>
      </div>
      <InlineHelpText
        ref={checkInTimeHelpTextRef}
        toggleFunction={toggleCheckInTimeHelpText}
        message={checkInTimeHelpText}
      />
    </div>
  ) : (
    ''
  );

  let exclusionDateStatus = '';
  if (exclusion_start_time && exclusion_end_time) {
    const exclusionStart = new Date(exclusion_start_time);
    const exclStartTimeLocal = exclusion_start_time.split('-');
    exclusionStart.setFullYear(exclStartTimeLocal[0]);
    exclusionStart.setMonth(exclStartTimeLocal[1] - 1);
    exclusionStart.setDate(exclStartTimeLocal[2]);
    exclusionStart.setMinutes(0);
    exclusionStart.setHours(0);
    exclusionStart.setSeconds(0);

    const exclusionEnd = new Date(exclusion_end_time);
    const exclEndTimeLocal = exclusion_end_time.split('-');
    exclusionEnd.setFullYear(exclEndTimeLocal[0]);
    exclusionEnd.setMonth(exclEndTimeLocal[1] - 1);
    exclusionEnd.setDate(exclEndTimeLocal[2]);
    exclusionEnd.setMinutes(0);
    exclusionEnd.setHours(0);
    exclusionEnd.setSeconds(0);

    const currentTime = new Date();
    currentTime.setMinutes(0);
    currentTime.setHours(0);
    currentTime.setSeconds(0);

    if (exclusionEnd <= currentTime) { 
      //if end is less or equal to today, they are no longer being excluded so its in the past
      exclusionDateStatus = 'past';
    } else if (exclusionStart > currentTime) {
      //if start is greater than today, their exclusion hasn't started yet so its in the future
      exclusionDateStatus = 'future';
    } else if (exclusionStart <= currentTime && exclusionEnd > currentTime) {
      //if start time is greater or equal to today, they are currently being excluded
      //if above is true AND end is greater than today, their exclusion hasn't ended yet
      //If both of those are true, they are currently being excluded
      exclusionDateStatus = 'current';
    }
  }
  const checkinExclude = exclusion_start_time ? (
    <React.Fragment>
      <div
        className="content-display__column"
        data-test="exclusion-start-container">
        <div className="content-display__item-pill-container content-display__item-pill-container--content-container">
          <div className="content-display__column">
            <div className="content-display__item-container">
              {exclusionDateStatus === 'future' ? (
                <React.Fragment>
                  <div className="item-container__info-container text-success">
                    <SvgExclamationPointIcon />
                  </div>
                  <div className="item-container__main-container">
                    <div className="text-large text-light text-success">
                      <FormattedMessage
                        id="userCheckinPreferences.userWillBeExcludedText"
                        defaultMessage="This user is scheduled to be excluded from checking in."
                        description="This user will receive check-in report emails"
                        data-test="exclusion-future"
                      />
                    </div>
                  </div>
                </React.Fragment>
              ) : (
                ''
              )}
              {exclusionDateStatus === 'current' ? (
                <React.Fragment>
                  <div className="item-container__info-container">
                    <SvgExclamationPointIcon />
                  </div>
                  <div className="item-container__main-container">
                    <div className="text-large text-light text-alert">
                      <FormattedMessage
                        id="userCheckinPreferences.userIsExcludedText"
                        defaultMessage="This user is <b>currently excluded</b> from checking in and will remain so until the end date listed below."
                        description="This user will receive check-in report emails"
                        data-test="exclusion-current"
                        values={{
                          b: (msg) => (
                            <span className="item-container__text-highlighted item-container__text-highlighted--alert">
                              {msg}
                            </span>
                          ),
                        }}
                      />
                    </div>
                    <div
                      className="item-container__secondary-text text-medium text-bold"
                      data-test="alert-description-container"></div>
                  </div>
                </React.Fragment>
              ) : (
                ''
              )}
              {exclusionDateStatus === 'past' ? (
                <React.Fragment>
                  <div className="item-container__main-container">
                    <div className="item-container__main-text text-large text-light">
                      <FormattedMessage
                        id="userCheckinPreferences.checkInExcludePastText"
                        defaultMessage="The last time this user was excluded from the check in report:"
                        description="This user will be excluded from the check-in report"
                        data-test="exclusion-past"
                      />
                    </div>
                  </div>
                </React.Fragment>
              ) : (
                ''
              )}
              <div className="item-container__info-container">
                <HelpIcon
                  styles="margin-left-5"
                  onClick={() =>
                    checkInExcludeHelpTextRef.current.toggleHelpText()
                  }
                  toggled={showCheckInExcludeHelpText}
                />
              </div>
            </div>
          </div>
          <div className="content-display__column content-display__column--1-2">
            <div className="content-display__item-container">
              <div className="item-container__main-container vertical-center">
                <div className="item-container__main-text text-large text-light">
                  <FormattedMessage
                    id="userCheckinPreferences.startDate"
                    defaultMessage="Start Date:"
                    description="The beginning date of the date range."
                  />
                  <div className="item-container__text-highlighted text-center">
                    {exclusion_start_time ? (
                      <FormattedDate
                        value={new Date(exclusion_start_time)}
                        data-test="exclusion-start-date"
                        timeZone="UTC"
                      />
                    ) : (
                      <span className="text-dimmed">
                        <FormattedMessage
                          id="userCheckinPreferences.noDateSet"
                          defaultMessage="No date set"
                          description="Message when no date is set"
                        />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="content-display__column content-display__column--1-2">
            <div className="content-display__item-container">
              <div className="item-container__main-container vertical-center">
                <div className="item-container__main-text text-large text-light">
                  <FormattedMessage
                    id="userCheckinPreferences.endDate"
                    defaultMessage="End Date:"
                    description="The end date of the date range."
                  />
                  <div className="item-container__text-highlighted text-center">
                    {exclusion_end_time ? (
                      <FormattedDate
                        value={new Date(exclusion_end_time)}
                        data-test="exclusion-end-date"
                        timeZone="UTC"
                      />
                    ) : (
                      <span className="text-dimmed">
                        <FormattedMessage
                          id="userCheckinPreferences.noDateSet"
                          defaultMessage="No date set"
                          description="Message when no date is set"
                        />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="content-display__column content-display__column--1-2">
            <div className="content-display__item-container">
              <div className="item-container__main-container vertical-center">
                <div className="item-container__main-text text-large text-light">
                  <FormattedMessage
                    id="userCheckinPreferences.checkInExcludeStatusText"
                    defaultMessage="While the user is excluded from check-ins, their status will be:"
                    description="The user's status."
                  />
                  <div
                    className="item-container__text-highlighted text-center"
                    data-test="exclusion-future-status">
                    {exclusion_start_status
                      ? userStatuses[exclusion_start_status].description
                      : ' '}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="content-display__column content-display__column--1-2">
            <div className="content-display__item-container">
              <div className="item-container__main-container vertical-center">
                <div className="item-container__main-text text-large text-light">
                  <FormattedMessage
                    id="userCheckinPreferences.checkInExcludeEndStatusText"
                    defaultMessage="When the exclusion is over, the user's status will be:"
                    description="The user's status."
                  />
                  <div
                    className="item-container__text-highlighted text-center"
                    data-test="exclusion-end-status">
                    {exclusion_end_status
                      ? userStatuses[exclusion_end_status].description
                      : ' '}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <InlineHelpText
          ref={checkInExcludeHelpTextRef}
          toggleFunction={toggleShowCheckInExcludeHelpText}
          message={checkInExcludeHelpText}
        />
      </div>
    </React.Fragment>
  ) : (
    ''
  );

  const checkinModesDisplay = (
    <div
      className="content-display__column content-display__column--1-2"
      data-test="checkin-modes-container">
      <div className="content-display__item-pill-container">
        <div className="item-container__main-container">
          <div className="item-container__main-text text-large text-light">
            <FormattedMessage
              id="userCheckinPreferences.checkInModesText"
              defaultMessage="Check-in Modes"
              description="The check-in instructions"
              data-test="checkin-modes"
            />
          </div>
          <div
            className="item-container__secondary-text text-medium text-bold"
            data-test="checkin-modes-list">
            {checkin_modes.length > 0 ? (
              checkin_modes.map((mode, index) => (
                <div data-test="checkin-mode" className="" key={index}>
                  {checkinModes[mode].mode_name}
                </div>
              ))
            ) : (
              <div className="">
                <span
                  className="text-dimmed margin-right-10"
                  data-test="no-users">
                  <FormattedMessage
                    id="userCheckinPreferences.checkInModesTextFalse"
                    defaultMessage="No modes are assigned"
                    description="This user  will receive check-in report emails"
                    data-test="not-assigned-alert-groups"
                    values={{
                      b: (msg) => (
                        <span className="item-container__text-highlighted item-container__text-highlighted--alert">
                          {msg}
                        </span>
                      ),
                    }}
                  />
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="item-container__info-container">
          <HelpIcon
            styles="margin-left-5"
            onClick={() => checkInModesHelpTextRef.current.toggleHelpText()}
            toggled={showCheckInModesHelpText}
          />
        </div>
      </div>
      <InlineHelpText
        ref={checkInModesHelpTextRef}
        toggleFunction={toggleCheckInModesHelpText}
        message={checkInModesHelpText}
      />
    </div>
  );

  /**
   * Function to generate the units table
   * @returns {object} - The table of units
   */
  const alertDevicesTable = () => {
    const alertDeviceDetailsList = [];
    const allDevices = [
      ...(userObj.available_checkin_devices || []),
      ...(userObj.alert_devices || []),
    ];
    const seen = new Set();
    const deduped = allDevices.filter((d) => {
      if (seen.has(d.alert_device_id)) return false;
      seen.add(d.alert_device_id);
      return true;
    });
    const alertDeviceList = deduped.filter(
      (d) => d.alert_device_id === checkin_device
    );
    alertDeviceList.map((details) => {
      let deviceLink = (
        <AccessControl
          data-test="access-control"
          feature={permissions.static.devices.alert.editAlertDevice}>
          <FormattedMessage
            id="userCheckinPreferences.viewAlertDeviceText"
            defaultMessage="View Device"
            description="Button to view full unit info">
            {(txt) => (
              <div className="width-100 flex-end height-100 align-center">
                <a
                  data-test="view-alert-device-link"
                  className="text-small margin-right-10 link-text align-center"
                  href={`/admin/alert_dev_edit.php?&alert_device_options=&mode=edit&edit_row[alert_device_id]=${details.alert_device_id}&edit_row[alert_device_type]=${details.alert_device_type}`}>
                  {txt} <SvgOpenExternal className="margin-left-5" />
                </a>
              </div>
            )}
          </FormattedMessage>
        </AccessControl>
      );
      if (
        details.alert_device_type === 3 ||
        details.alert_device_type === 4 ||
        details.alert_device_type === 24 ||
        details.alert_device_type === 25
      ) {
        deviceLink = (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.devices.alert.editAlertDevice}>
            <FormattedMessage
              id="userCheckinPreferences.viewAlertDevice"
              defaultMessage="View Device"
              description="Button to view full unit info">
              {(txt) => (
                <div className="width-100 flex-end height-100 align-center">
                  <Link
                    data-test="view-alert-device-link-typed"
                    to={`/devices/alert/${details.alert_device_id}/view/`}
                    className="text-small margin-right-10 link-text align-center">
                    {txt} <SvgOpenExternal className="margin-left-5" />
                  </Link>
                </div>
              )}
            </FormattedMessage>
          </AccessControl>
        );
      }

      alertDeviceDetailsList.push({
        id: details.alert_device_id,
        description: details.alert_description,
        type: details.alert_device_type,
        view: deviceLink,
      });

      return '';
    });

    // map over the units and put into the format that is required for the table component
    if (alertDeviceDetailsList.length > 0) {
      const devicesArray = alertDeviceDetailsList.map((d) => {
        let deviceType = '';
        if (alertDeviceTypes[d.type]) {
          deviceType = alertDeviceTypes[d.type].description;
        }
        return {
          description: {
            sortBy: d.description.toUpperCase(),
            display: d.description,
          },
          type: {
            sortBy: deviceType.toUpperCase(),
            display: deviceType,
          },
          view: {
            sortBy: '',
            display: d.view,
          },
        };
      });
      // Take the array and filter based on the text entered in the search bar.

      const tableHeadings = [
        {
          isSortable: true,
          sortBy: 'description',
          display: descriptionText,
        },
        {
          isSortable: true,
          sortBy: 'type',
          display: typeText,
        },
        {
          isSortable: false,
          sortBy: 'view',
          display: '',
        },
      ];

      // generates the actual table
      const table = (
        <Table
          hasTableHead={false}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="description"
          tableData={devicesArray}
          tableStyles="table-layout__3-column"
          data-test="checkin-devices-table"
        />
      );
      return (
        <div
          className="content-display__column"
          data-test="alert-devices-container">
          <div className="content-display__item-pill-container">
            <div className="item-container__main-container">
              <div className="item-container__main-text text-large text-light flex-spread margin-bottom-10">
                <div className="align-center">
                  <FormattedMessage
                    id="userDevices.checkinDeviceText"
                    defaultMessage="Check-in Device:"
                    description="The assigned alert devices for this unit"
                  />
                  <HelpIcon
                    onClick={() =>
                      checkinDeviceHelpTextRef.current.toggleHelpText()
                    }
                    toggled={showCheckinDeviceHelpText}
                    styles="margin-left-5"
                  />
                </div>
              </div>
              <div className="item-container__secondary-text text-medium text-bold flex-row align-center">
                {table}
              </div>
            </div>
            <div className="item-container__info-container"></div>
          </div>
          <InlineHelpText
            ref={checkinDeviceHelpTextRef}
            isToggled={toggleCheckinDeviceHelpText}
            message={checkinDeviceHelpText}
          />
        </div>
      );
    } else {
      return '';
    }
  };

  return (
    <div className="content-display__container" data-test="content-container">
      {unitCheckinCheck()}
      <div className="width-100 flex-spread align-center">
        <FormattedMessage
          id="userCheckinPreferences.userCheckInOptions"
          defaultMessage="User Check-In Options"
          description="The check-in options">
          {(txt) => (
            <span className="text-large text-light no-margin">{txt}: </span>
          )}
        </FormattedMessage>
      </div>
      {checkin_profile ? (
        <React.Fragment>
          {checkInReminder}
          {checkinTime}
          {checkinModesDisplay}
          {checkinExclude}
          {alertDevicesTable()}
          <AccessControl
            data-test="access-control"
            feature={permissions.static.users.editCheckinExclusions}
            userToEdit={user}
            permissionMessage={false}>
            {userObj.is_active ? (
              <div className="width-100 flex-spread align-center">
                <div></div>
                <FormattedMessage
                  id="userCheckinPreferences.userCheckInOptionsEdit"
                  defaultMessage="Edit Check-In Options"
                  data-test="edit-link-text"
                  description="The check-in options">
                  {(txt) => (
                    <Link
                      data-test="edit-link"
                      to={`/users/${user}/edit/?mode=checkInPreferences`}
                      state={{
                        modal: true,
                        previous: currentLocation,
                      }}
                      className="link-button button-secondary button-sm text-small">
                      {txt}
                    </Link>
                  )}
                </FormattedMessage>
              </div>
            ) : (
              ''
            )}
          </AccessControl>
        </React.Fragment>
      ) : (
        <div className="content-display__column">
          <div className="content-display__item-pill-container">
            <div className="item-container__main-container">
              <div className="text-large text-deep-blue">
                <FormattedMessage
                  id="userCheckinPreferences.noUserCheckInOptions"
                  defaultMessage="This user does not have a Check-In profile"
                  description="The check-in options"></FormattedMessage>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const mapStateToProps = (state, ownProps) => {
  return {
    alertDeviceTypes: state.devices.alertDeviceTypes,
    checkinCall: state.system.siteInfo.checkin_reminder_time,
  };
};

export default connect(mapStateToProps)(UnconnectedUserCheckinPreferences);
